import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Compass } from "@phosphor-icons/react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1551632811-561732d1e306?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80";

export default function Login() {
  const { login, resendVerification, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showResend, setShowResend] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setShowResend(false);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/");
  };

  const needsVerify = showResend || (error && error.toLowerCase().includes("confirm your email"));
  const resend = async () => {
    const ok = await resendVerification(email);
    toast[ok ? "success" : "error"](ok ? "Confirmation email re-sent" : "Could not resend right now");
  };

  useEffect(() => {
    if (error && error.toLowerCase().includes("confirm your email")) setShowResend(true);
  }, [error]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Hikers" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#1A1C19]/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2"><Compass size={28} weight="duotone" className="text-[#E8B291]" /><span className="font-display text-2xl font-bold">TravelNext</span></Link>
          <div>
            <h2 className="font-display text-4xl font-bold leading-tight">Welcome back,<br />wanderer.</h2>
            <p className="text-white/80 mt-3 max-w-sm">Your boards, plans and AI conversations are waiting.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8"><Compass size={26} weight="duotone" className="text-[#2E523A]" /><span className="font-display text-xl font-bold">TravelNext</span></Link>
          <h1 className="font-display text-3xl font-bold">Sign in</h1>
          <p className="text-[#63675E] mt-1 mb-8">Don't have an account? <Link to="/register" className="text-[#2E523A] font-semibold" data-testid="go-register">Create one</Link></p>

          <form onSubmit={submit} className="space-y-5" autoComplete="off">
            {error && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-3" data-testid="login-error">{error}</div>}
            {needsVerify && (
              <button type="button" onClick={resend} className="text-sm font-semibold text-[#2E523A] hover:underline" data-testid="login-resend-verification">
                Resend confirmation email
              </button>
            )}
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" required autoComplete="off" value={email} onChange={(e) => { setEmail(e.target.value); setError(""); }} placeholder="you@example.com" className="h-12" data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" required autoComplete="new-password" value={password} onChange={(e) => { setPassword(e.target.value); setError(""); }} placeholder="••••••••" className="h-12" data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-[#2E523A] hover:bg-[#26442f] h-12 text-base" data-testid="login-submit-btn">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
